// Admin-only job cleanup. Firestore rules reject these deletes unless the
// signed-in user's users/{uid}.role is "admin". This module never deletes
// every job: the caller passes the ids the admin selected.
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import { ref as storageRef, listAll, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import {
  LINKED_JOB_COLLECTIONS,
  deletePlan,
  jobImagePath,
  toAdminJobRow,
} from '../utils/adminJobCleanup';

const BATCH_LIMIT = 450;

function chunk(items, size) {
  const groups = [];
  for (let i = 0; i < items.length; i += size) groups.push(items.slice(i, i + size));
  return groups;
}

export async function listJobsForAdmin() {
  const jobSnap = await getDocs(collection(db, 'jobs'));
  const jobs = jobSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const customerIds = [...new Set(jobs.map((job) => job.customerId).filter(Boolean))];
  const users = new Map();
  await Promise.all(customerIds.map(async (uid) => {
    const snap = await getDoc(doc(db, 'users', uid));
    users.set(uid, snap.exists() ? snap.data() : null);
  }));

  const bidSnap = await getDocs(collection(db, 'bids'));
  const bidCounts = new Map();
  bidSnap.docs.forEach((d) => {
    const jobId = d.data().jobId;
    if (!jobId) return;
    bidCounts.set(jobId, (bidCounts.get(jobId) || 0) + 1);
  });

  return jobs
    .map((job) => toAdminJobRow(job, users.get(job.customerId) || null, bidCounts.get(job.id) || 0))
    .sort((a, b) => b.createdAtMs - a.createdAtMs || a.title.localeCompare(b.title));
}

async function deleteAllAt(folderRef) {
  const listed = await listAll(folderRef);
  await Promise.all(listed.items.map(async (item) => {
    try {
      await deleteObject(item);
    } catch (err) {
      if (err?.code !== 'storage/object-not-found') throw err;
    }
  }));
  for (const prefix of listed.prefixes) {
    await deleteAllAt(prefix);
  }
}

async function deleteJobImages(jobId) {
  const folder = storageRef(storage, jobImagePath(jobId));
  try {
    await deleteAllAt(folder);
  } catch (err) {
    if (err?.code === 'storage/object-not-found') return;
    const detail = err?.code || err?.message || 'unknown error';
    throw new Error(`Could not remove photos for job ${jobId} (${detail}), so the job was not deleted.`);
  }
}

async function linkedDocs(jobId) {
  const docsByCollection = {};
  for (const name of LINKED_JOB_COLLECTIONS) {
    const snap = await getDocs(query(collection(db, name), where('jobId', '==', jobId)));
    docsByCollection[name] = snap.docs.map((d) => d.id);
  }
  return docsByCollection;
}

async function commitPaths(paths) {
  for (const group of chunk(paths, BATCH_LIMIT)) {
    const batch = writeBatch(db);
    group.forEach((path) => {
      batch.delete(doc(db, ...path.split('/')));
    });
    await batch.commit();
  }
}

export async function deleteJobsAndLinkedData(jobIds) {
  if (!Array.isArray(jobIds)) {
    throw new Error('No jobs selected.');
  }
  const ids = [...new Set(jobIds.map((id) => String(id || '').trim()).filter(Boolean))];
  if (ids.length === 0) throw new Error('No jobs selected.');
  if (ids.some((id) => id.includes('/'))) throw new Error('Invalid job.');

  for (const jobId of ids) {
    await deleteJobImages(jobId);
    const paths = deletePlan(jobId, await linkedDocs(jobId));
    await commitPaths(paths);
  }
}
